import React from 'react'
import * as ROUTES from 'constants/routes'
import { Box, Button, Flex, Icon, Link, Text, Tooltip } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { ExternalLinkIcon, InfoIcon, TerminalIcon } from 'lucide-react'
import { PaginatedTable } from 'components/Table/PaginatedTable'
import { useFieldglassReferenceDataContextValue } from 'pages/Fieldglass/shared/context/FieldglassReferenceDataProvider'
import { ResolveFieldglassIssueButton } from 'pages/Fieldglass/shared/components/ResolveFieldglassIssueButton'
import { useFieldglassShiftIssuesContextValue } from '../context/FieldglassShiftIssuesProvider'

type FieldglassShiftIssuesItem = ReturnType<
  typeof useFieldglassShiftIssuesContextValue
>['state']['data']['items'][number]

type FieldglassOperationIssue = FieldglassShiftIssuesItem['issues'][number]

const _ISSUE_LINE_HEIGHT = 32

const IssueLine = ({ children }: { children: React.ReactNode }) => (
  <Flex
    alignItems="center"
    gap={2}
    height={_ISSUE_LINE_HEIGHT}
    style={{ whiteSpace: 'nowrap' }}
  >
    {children}
  </Flex>
)

const IssueCells = ({
  issues,
  render,
}: {
  issues: FieldglassOperationIssue[]
  render: (issue: FieldglassOperationIssue) => React.ReactNode
}) => (
  <Box>
    {issues.map((issue) => (
      <IssueLine key={issue.id}>{render(issue)}</IssueLine>
    ))}
  </Box>
)

const ViewJobPostingButton = ({
  companyGroupId,
  jobPostingId,
}: {
  companyGroupId: number
  jobPostingId: string | null
}) => {
  const {
    state: { companyGroups },
  } = useFieldglassReferenceDataContextValue()

  if (jobPostingId === null) {
    return null
  }

  const buyerUrl = companyGroups.find(
    (group) => group.companyGroupId === companyGroupId
  )?.buyerUrl

  if (buyerUrl === undefined) {
    return null
  }

  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={buyerUrl.replace(/\/$/, '')}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">Open Fieldglass</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const buildFieldglassCommandsUrl = ({
  shiftId,
  jobPostingId,
}: {
  shiftId: number
  jobPostingId: string | null
}) => {
  const params = new URLSearchParams({
    search: 'fieldglass',
    shift_id: shiftId.toString(),
    shifts: shiftId.toString(),
  })
  if (jobPostingId !== null) {
    params.set('job_posting_id', jobPostingId)
  }

  return `${ROUTES.COMMANDS}?${params.toString()}`
}

const RunCommandButton = ({
  shiftId,
  jobPostingId,
}: {
  shiftId: number
  jobPostingId: string | null
}) => {
  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={buildFieldglassCommandsUrl({ shiftId, jobPostingId })}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">Run command</Text>
        <Icon icon={TerminalIcon} size={12} />
      </Flex>
    </Button>
  )
}

const ViewShiftButton = ({ shiftId }: { shiftId: number }) => {
  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={ROUTES.SHIFT_DETAIL.replace(':shiftId', shiftId.toString())}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">View shift</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const columns: ColumnDef<FieldglassShiftIssuesItem>[] = [
  {
    header: 'Company Group',
    accessorKey: 'companyGroupName',
    cell: ({ row }) => (
      <Text>
        {row.original.companyGroupName ?? row.original.companyGroupId}
      </Text>
    ),
  },
  {
    header: 'Job Posting ID',
    accessorKey: 'jobPostingId',
    cell: ({ row }) =>
      row.original.jobPostingId === null ? (
        <Text style={{ whiteSpace: 'nowrap' }}>-</Text>
      ) : (
        <Link
          to={`${ROUTES.FIELDGLASS_JOB_POSTING}?job-posting-id=${encodeURIComponent(
            row.original.jobPostingId
          )}`}
          target="_blank"
          style={{ whiteSpace: 'nowrap' }}
        >
          {row.original.jobPostingId}
        </Link>
      ),
  },
  {
    header: 'Shift ID',
    accessorKey: 'shiftId',
    cell: ({ row }) => (
      <Link
        to={ROUTES.SHIFT_DETAIL.replace(
          ':shiftId',
          row.original.shiftId.toString()
        )}
        target="_blank"
      >
        {row.original.shiftId}
      </Link>
    ),
  },
  {
    header: 'Position',
    accessorKey: 'positionName',
    cell: ({ row }) => <Text>{row.original.positionName ?? '-'}</Text>,
  },
  {
    header: 'Issue',
    cell: ({ row }) => (
      <IssueCells
        issues={row.original.issues}
        render={(issue) => (
          <>
            <Text fontSize={1}>{issue.issueTypeLabel}</Text>
            <Tooltip content={issue.message}>
              <Icon icon={InfoIcon} size={14} />
            </Tooltip>
            <ResolveFieldglassIssueButton
              issueId={issue.id}
              issueType={issue.issueType}
            />
          </>
        )}
      />
    ),
  },
  {
    header: 'Worker',
    cell: ({ row }) => (
      <IssueCells
        issues={row.original.issues}
        render={(issue) =>
          issue.workerId === null ? (
            <Text color="lightText">-</Text>
          ) : (
            <Link
              to={ROUTES.USER_DETAIL.replace(
                ':userId',
                issue.workerId.toString()
              )}
              target="_blank"
            >
              {issue.workerName ?? issue.workerId}
            </Link>
          )
        }
      />
    ),
  },
  {
    header: 'Actions',
    cell: ({ row }) => (
      <Flex gap={2} flexWrap="wrap">
        <ViewJobPostingButton
          companyGroupId={row.original.companyGroupId}
          jobPostingId={row.original.jobPostingId}
        />
        <ViewShiftButton shiftId={row.original.shiftId} />
        <RunCommandButton
          shiftId={row.original.shiftId}
          jobPostingId={row.original.jobPostingId}
        />
      </Flex>
    ),
  },
]

export const FieldglassShiftIssuesTable = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useFieldglassShiftIssuesContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No shift issues found."
      emptyStateHeight={50}
    />
  )
}
